import type { Point, Wall, Room } from '../types';
import type { WallWithTempId } from '../types';
import { pointsAreEqual } from './floorPlanUtils';

/**
 * Generate a unique ID for cloned points
 */
const generateUniquePointId = (): string => {
  return `point_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a point identifier from coordinates (for tracking shared points)
 */
export const getPointId = (point: Point): string => {
  return `${point.x.toFixed(3)},${point.y.toFixed(3)}`;
};

/**
 * Get all rooms that share a specific point by coordinates
 */
export const getRoomsSharingPoint = (
  point: Point,
  allRooms: Room[],
  tolerance: number = 0.001
): Room[] => {
  const roomsUsingPoint: Room[] = [];
  
  for (const room of allRooms) {
    const usesPoint = room.walls.some(wall => 
      pointsAreEqual(wall.start, point, tolerance) || 
      pointsAreEqual(wall.end, point, tolerance)
    );
    
    if (usesPoint) {
      roomsUsingPoint.push(room);
    }
  }
  
  console.log('🔍 ROOMS SHARING POINT', {
    point: getPointId(point),
    roomsUsingPoint: roomsUsingPoint.map(r => r.name),
    sharedCount: roomsUsingPoint.length
  });
  
  return roomsUsingPoint;
};

/**
 * Get all unique points from a wall
 */
export const getWallPoints = (wall: WallWithTempId): Point[] => {
  return [wall.start, wall.end];
};

/**
 * Get all unique points from a room's walls
 */
export const getRoomPoints = (room: Room): Point[] => {
  const points: Point[] = [];
  const pointIds = new Set<string>();
  
  for (const wall of room.walls) {
    for (const point of [wall.start, wall.end]) {
      const pointId = getPointId(point);
      if (!pointIds.has(pointId)) {
        pointIds.add(pointId);
        points.push(point);
      }
    }
  }
  
  return points;
};

/**
 * Clone a point with slightly offset coordinates for true spatial separation
 */
export const clonePoint = (originalPoint: Point): Point => {
  // Create a small offset to physically separate the points
  // This ensures rooms can move independently without overlapping
  const offset = 0.5; // Small offset in the same units as the floor plan
  return {
    x: originalPoint.x + offset,
    y: originalPoint.y + offset,
  };
};

/**
 * Update all walls in a room to use a new point instead of an old one
 */
export const updateRoomPointsInWalls = (
  roomWalls: Wall[],
  allWalls: WallWithTempId[],
  oldPoint: Point,
  newPoint: Point,
  tolerance: number = 0.001
): { updatedWalls: WallWithTempId[]; affectedWallIds: string[] } => {
  const updatedWalls = [...allWalls];
  const affectedWallIds: string[] = [];
  
  // Find wall tempIds that belong to this room
  const roomWallTempIds = roomWalls.map(roomWall => {
    const match = allWalls.find(cw => 
      (pointsAreEqual(cw.start, roomWall.start, tolerance) && pointsAreEqual(cw.end, roomWall.end, tolerance)) ||
      (pointsAreEqual(cw.start, roomWall.end, tolerance) && pointsAreEqual(cw.end, roomWall.start, tolerance))
    );
    return match?.tempId;
  }).filter(Boolean) as string[];
  
  // Update walls that belong to this room and use the old point
  for (let i = 0; i < updatedWalls.length; i++) {
    const wall = updatedWalls[i];
    if (wall.tempId && roomWallTempIds.includes(wall.tempId)) {
      let wallUpdated = false;
      const updatedWall = { ...wall };
      
      if (pointsAreEqual(wall.start, oldPoint, tolerance)) {
        updatedWall.start = { ...newPoint };
        wallUpdated = true;
        console.log('🔄 UPDATED WALL START POINT', {
          wallId: wall.tempId,
          oldStart: getPointId(oldPoint),
          newStart: getPointId(newPoint)
        });
      }
      
      if (pointsAreEqual(wall.end, oldPoint, tolerance)) {
        updatedWall.end = { ...newPoint };
        wallUpdated = true;
        console.log('🔄 UPDATED WALL END POINT', {
          wallId: wall.tempId,
          oldEnd: getPointId(oldPoint),
          newEnd: getPointId(newPoint)
        });
      }
      
      if (wallUpdated) {
        updatedWalls[i] = updatedWall;
        affectedWallIds.push(wall.tempId);
      }
    }
  }
  
  console.log('🏠 ROOM POINTS UPDATED', {
    oldPoint: getPointId(oldPoint),
    newPoint: getPointId(newPoint),
    affectedWallIds,
    roomWallTempIds
  });
  
  return { updatedWalls, affectedWallIds };
};

/**
 * Find all shared points on a wall that need decoupling
 */
export const findSharedPointsOnWall = (
  draggedWall: WallWithTempId,
  movingRoom: Room,
  allRooms: Room[],
  tolerance: number = 0.001
): { sharedPoints: Point[]; pointRoomMap: Map<string, Room[]> } => {
  const wallPoints = getWallPoints(draggedWall);
  const sharedPoints: Point[] = [];
  const pointRoomMap = new Map<string, Room[]>();
  
  for (const point of wallPoints) {
    const roomsUsingPoint = getRoomsSharingPoint(point, allRooms, tolerance);
    const pointId = getPointId(point);
    pointRoomMap.set(pointId, roomsUsingPoint);
    
    // A point is shared if more than one room uses it
    if (roomsUsingPoint.length > 1) {
      sharedPoints.push(point);
      console.log('🔍 SHARED POINT DETECTED', {
        pointId,
        roomsUsing: roomsUsingPoint.map(r => r.name),
        movingRoom: movingRoom.name
      });
    }
  }
  
  return { sharedPoints, pointRoomMap };
};

/**
 * Find wall intersection points along a wall's length
 * This detects when a wall intersects with other walls at intermediate points
 */
export const findWallIntersectionPoints = (
  draggedWall: WallWithTempId,
  allWalls: WallWithTempId[],
  tolerance: number = 0.5
): { intersectionPoints: Point[]; intersectedWalls: WallWithTempId[] } => {
  const intersectionPoints: Point[] = [];
  const intersectedWalls: WallWithTempId[] = [];
  
  // Check each wall for intersection with the dragged wall
  for (const wall of allWalls) {
    if (wall.tempId === draggedWall.tempId) continue; // Skip self
    
    // Check if walls intersect at any point along their length
    const intersection = findWallIntersection(draggedWall, wall, tolerance);
    if (intersection) {
      intersectionPoints.push(intersection);
      intersectedWalls.push(wall);
      
      console.log('🔍 WALL INTERSECTION FOUND', {
        draggedWall: `${getPointId(draggedWall.start)} -> ${getPointId(draggedWall.end)}`,
        intersectedWall: `${getPointId(wall.start)} -> ${getPointId(wall.end)}`,
        intersectionPoint: getPointId(intersection)
      });
    }
  }
  
  return { intersectionPoints, intersectedWalls };
};

/**
 * Find intersection point between two walls
 */
export const findWallIntersection = (
  wall1: WallWithTempId,
  wall2: WallWithTempId,
  tolerance: number = 0.5
): Point | null => {
  // Check if walls are parallel (within tolerance)
  const wall1Vector = { x: wall1.end.x - wall1.start.x, y: wall1.end.y - wall1.start.y };
  const wall2Vector = { x: wall2.end.x - wall2.start.x, y: wall2.end.y - wall2.start.y };
  
  // Check if lines are parallel
  const denominator = wall1Vector.x * wall2Vector.y - wall1Vector.y * wall2Vector.x;
  if (Math.abs(denominator) < 0.001) {
    // Lines are parallel, check if they overlap
    return checkParallelWallOverlap(wall1, wall2, tolerance);
  }
  
  // Calculate intersection point
  const t1 = ((wall2.start.x - wall1.start.x) * wall2Vector.y - (wall2.start.y - wall1.start.y) * wall2Vector.x) / denominator;
  const t2 = ((wall2.start.x - wall1.start.x) * wall1Vector.y - (wall2.start.y - wall1.start.y) * wall1Vector.x) / denominator;
  
  // Check if intersection is within both line segments
  if (t1 >= 0 && t1 <= 1 && t2 >= 0 && t2 <= 1) {
    const intersectionPoint = {
      x: wall1.start.x + t1 * wall1Vector.x,
      y: wall1.start.y + t1 * wall1Vector.y
    };
    
    return intersectionPoint;
  }
  
  return null;
};

/**
 * Check if two parallel walls overlap and return the connection point
 */
export const checkParallelWallOverlap = (
  wall1: WallWithTempId,
  wall2: WallWithTempId,
  tolerance: number = 0.5
): Point | null => {
  // Check if walls are on the same line (within tolerance)
  const distanceFromWall1ToWall2Start = distanceFromPointToWall(wall2.start, wall1, tolerance);
  const distanceFromWall1ToWall2End = distanceFromPointToWall(wall2.end, wall1, tolerance);
  
  if (distanceFromWall1ToWall2Start <= tolerance || distanceFromWall1ToWall2End <= tolerance) {
    // Walls are on the same line, check for overlap
    const wall1Start = wall1.start;
    const wall1End = wall1.end;
    const wall2Start = wall2.start;
    const wall2End = wall2.end;
    
    // Project points onto the wall direction to check overlap
    const wall1Vector = { x: wall1End.x - wall1Start.x, y: wall1End.y - wall1Start.y };
    const wall1Length = Math.hypot(wall1Vector.x, wall1Vector.y);
    
    if (wall1Length === 0) return null;
    
    const wall1Unit = { x: wall1Vector.x / wall1Length, y: wall1Vector.y / wall1Length };
    
    // Project wall2 points onto wall1's direction
    const wall2StartProjection = (wall2Start.x - wall1Start.x) * wall1Unit.x + (wall2Start.y - wall1Start.y) * wall1Unit.y;
    const wall2EndProjection = (wall2End.x - wall1Start.x) * wall1Unit.x + (wall2End.y - wall1Start.y) * wall1Unit.y;
    
    // Check if projections overlap with wall1 (0 to wall1Length)
    const overlapStart = Math.max(0, Math.min(wall2StartProjection, wall2EndProjection));
    const overlapEnd = Math.min(wall1Length, Math.max(wall2StartProjection, wall2EndProjection));
    
    if (overlapStart <= overlapEnd) {
      // There's an overlap, return the connection point
      const connectionProjection = (overlapStart + overlapEnd) / 2;
      return {
        x: wall1Start.x + connectionProjection * wall1Unit.x,
        y: wall1Start.y + connectionProjection * wall1Unit.y
      };
    }
  }
  
  return null;
};

/**
 * Calculate distance from a point to a wall
 */
export const distanceFromPointToWall = (
  point: Point,
  wall: WallWithTempId,
  tolerance: number = 0.5
): number => {
  const wallVector = { x: wall.end.x - wall.start.x, y: wall.end.y - wall.start.y };
  const wallLength = Math.hypot(wallVector.x, wallVector.y);
  
  if (wallLength === 0) {
    return Math.hypot(point.x - wall.start.x, point.y - wall.start.y);
  }
  
  const wallUnit = { x: wallVector.x / wallLength, y: wallVector.y / wallLength };
  const pointVector = { x: point.x - wall.start.x, y: point.y - wall.start.y };
  
  // Project point onto wall direction
  const projection = pointVector.x * wallUnit.x + pointVector.y * wallUnit.y;
  
  // Clamp projection to wall segment
  const clampedProjection = Math.max(0, Math.min(wallLength, projection));
  
  // Calculate closest point on wall
  const closestPoint = {
    x: wall.start.x + clampedProjection * wallUnit.x,
    y: wall.start.y + clampedProjection * wallUnit.y
  };
  
  return Math.hypot(point.x - closestPoint.x, point.y - closestPoint.y);
};

/**
 * Main function: Decouple shared points before drag starts
 * This creates independent point copies for the moving room
 */
export const decoupleSharedPointsForDrag = (
  draggedWall: WallWithTempId,
  movingRoom: Room,
  allRooms: Room[],
  allWalls: WallWithTempId[],
  tolerance: number = 0.001
): { 
  updatedWalls: WallWithTempId[]; 
  decoupledPoints: { original: Point; cloned: Point; pointId: string }[];
  affectedWallIds: string[];
  requiresPartialDrag?: boolean;
  intersectionPoints?: Point[];
} => {
  console.log('🔪 STARTING POINT-BASED DECOUPLING', {
    draggedWall: `${getPointId(draggedWall.start)} -> ${getPointId(draggedWall.end)}`,
    movingRoom: movingRoom.name,
    totalRooms: allRooms.length
  });
  
  // Check for wall intersections at intermediate points
  const { intersectionPoints, intersectedWalls } = findWallIntersectionPoints(
    draggedWall,
    allWalls,
    tolerance
  );
  
  // If we found intersections, this requires partial dragging
  if (intersectionPoints.length > 0) {
    console.log('🔍 WALL INTERSECTIONS DETECTED - REQUIRES PARTIAL DRAG', {
      intersectionCount: intersectionPoints.length,
      intersectionPoints: intersectionPoints.map(p => getPointId(p)),
      intersectedWalls: intersectedWalls.map(w => w.tempId)
    });
    
    return {
      updatedWalls: allWalls,
      decoupledPoints: [],
      affectedWallIds: [],
      requiresPartialDrag: true,
      intersectionPoints
    };
  }
  
  // Original endpoint-based decoupling logic
  const { sharedPoints, pointRoomMap } = findSharedPointsOnWall(
    draggedWall, 
    movingRoom, 
    allRooms, 
    tolerance
  );
  
  if (sharedPoints.length === 0) {
    console.log('✅ NO SHARED POINTS FOUND - No decoupling needed');
    return { 
      updatedWalls: allWalls, 
      decoupledPoints: [],
      affectedWallIds: []
    };
  }
  
  let updatedWalls = [...allWalls];
  const decoupledPoints: { original: Point; cloned: Point; pointId: string }[] = [];
  const allAffectedWallIds: string[] = [];
  
  // Decouple each shared point
  for (const sharedPoint of sharedPoints) {
    const pointId = getPointId(sharedPoint);
    const roomsUsingThisPoint = pointRoomMap.get(pointId) || [];
    
    console.log('🔄 DECOUPLING POINT', {
      pointId,
      roomsUsing: roomsUsingThisPoint.map(r => r.name),
      movingRoom: movingRoom.name
    });
    
    // Create a cloned point for the moving room
    const clonedPoint = clonePoint(sharedPoint);
    decoupledPoints.push({ 
      original: sharedPoint, 
      cloned: clonedPoint, 
      pointId 
    });
    
    // Update the moving room's walls to use the cloned point
    const { updatedWalls: newWalls, affectedWallIds } = updateRoomPointsInWalls(
      movingRoom.walls,
      updatedWalls,
      sharedPoint,
      clonedPoint,
      tolerance
    );
    
    updatedWalls = newWalls;
    allAffectedWallIds.push(...affectedWallIds);
    
    console.log('✅ POINT DECOUPLED', {
      originalPointId: pointId,
      clonedPointId: getPointId(clonedPoint),
      movingRoom: movingRoom.name,
      affectedWallIds,
      otherRoomsStillUsingOriginal: roomsUsingThisPoint
        .filter(r => r.stableId !== movingRoom.stableId)
        .map(r => r.name)
    });
  }
  
  console.log('🎯 POINT DECOUPLING COMPLETE', {
    totalDecoupledPoints: decoupledPoints.length,
    totalAffectedWalls: allAffectedWallIds.length,
    movingRoom: movingRoom.name,
    decoupledPointIds: decoupledPoints.map(dp => dp.pointId)
  });
  
  return { 
    updatedWalls, 
    decoupledPoints,
    affectedWallIds: allAffectedWallIds
  };
}; 